import { openDB } from "idb";

/**
 * Main-thread asset cache backed by IndexedDB.
 *
 * This replaces the old `assets.worker.js`. IndexedDB is already asynchronous
 * and does not block the UI thread, so the worker/message-passing layer only
 * added latency and race conditions (writes were not awaited before reads).
 *
 * The DB name is kept identical to the previous implementation so existing
 * offline caches are preserved across the upgrade.
 */

const DB_VERSION = 4;

// One shared connection per user account.
const dbPromises = new Map();

// The server `/objects` delta endpoint filters on `dt_tracker > last_update`,
// NOT on `dt_server`. Deriving the watermark from `dt_server` is what caused
// "archive" (frozen) positions: a device's GPS time is often behind its server
// time, so the asset fell below the watermark and was skipped forever.
// We also step a small buffer back to absorb device clock skew. Re-sending a
// few already-known rows is harmless because we upsert by imei.
const WATERMARK_BUFFER_MS = 2 * 60 * 1000; // 2 minutes

function dbName(userid) {
  return "trackingDB_" + (userid || "default");
}

export function getDB(userid) {
  const name = dbName(userid);

  if (!dbPromises.has(name)) {
    dbPromises.set(
      name,
      openDB(name, DB_VERSION, {
        upgrade(db, oldVersion, newVersion, tx) {
          if (!db.objectStoreNames.contains("assets")) {
            const store = db.createObjectStore("assets", { keyPath: "imei" });
            store.createIndex("group", "group_name");
            store.createIndex("plate_number", "plate_number");
            store.createIndex("dt_tracker", "dt_tracker");
            store.createIndex("dt_server", "dt_server");
          } else {
            // Ensure newer indexes exist on databases created by older versions.
            const store = tx.objectStore("assets");
            if (!store.indexNames.contains("dt_tracker"))
              store.createIndex("dt_tracker", "dt_tracker");
            if (!store.indexNames.contains("dt_server"))
              store.createIndex("dt_server", "dt_server");
          }

          // Groups are always derived from the assets store, so recreating the
          // group cache purges any stale/empty groups left behind previously.
          // Asset rows (the offline cache) are intentionally preserved.
          if (db.objectStoreNames.contains("groups")) db.deleteObjectStore("groups");
          db.createObjectStore("groups", { keyPath: "name" });
        },
      })
    );
  }

  return dbPromises.get(name);
}

/* ------------------------------------------------------------------ */
/* internal helpers                                                    */
/* ------------------------------------------------------------------ */

async function upsertAssets(db, objects) {
  const tx = db.transaction("assets", "readwrite");
  const store = tx.objectStore("assets");

  for (let i = 0; i < objects.length; i++) {
    const obj = objects[i];
    if (!obj || !obj.imei) continue;
    if (!obj.group_name) obj.group_name = "No Group";
    // Stamp the merge time so the "last seen" label can still age on the client
    // when the absolute `dt_tracker_utc` is absent (see relativeAge()).
    obj.last_update_at = Date.now();
    await store.put(obj);
  }

  await tx.done;
}

async function pruneMissing(db, keepImeis) {
  const tx = db.transaction("assets", "readwrite");
  const store = tx.objectStore("assets");

  let cursor = await store.openCursor();
  while (cursor) {
    if (!keepImeis.has(cursor.value.imei)) await cursor.delete();
    cursor = await cursor.continue();
  }

  await tx.done;
}

// Groups are recomputed from the cached assets so counts can never drift and
// empty groups disappear as soon as their last asset is gone.
async function rebuildGroups(db) {
  const assets = await db.getAll("assets");
  const counts = new Map();

  for (let i = 0; i < assets.length; i++) {
    const name = assets[i].group_name || "No Group";
    counts.set(name, (counts.get(name) || 0) + 1);
  }

  const tx = db.transaction("groups", "readwrite");
  const store = tx.objectStore("groups");
  await store.clear();
  counts.forEach((count, name) => store.put({ name, count }));
  await tx.done;
}

function bucketByGroup(assets) {
  const bucket = new Map();

  for (let i = 0; i < assets.length; i++) {
    const name = assets[i].group_name || "No Group";
    if (!bucket.has(name)) bucket.set(name, []);
    bucket.get(name).push(assets[i]);
  }

  return bucket;
}

function buildGrouped(bucket) {
  return Array.from(bucket.keys())
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      text: name,
      model: false,
      count: bucket.get(name).length,
      children: bucket.get(name).sort((a, b) =>
        String(a.plate_number || a.name || "").localeCompare(
          String(b.plate_number || b.name || "")
        )
      ),
    }));
}

// "YYYY-MM-DD HH:mm:ss" shifted back by `ms`, matching the server's format.
function shiftBack(value, ms) {
  const parsed = new Date(String(value).replace(" ", "T"));
  if (isNaN(parsed.getTime())) return value;

  parsed.setTime(parsed.getTime() - ms);
  const pad = (n) => String(n).padStart(2, "0");

  return (
    parsed.getFullYear() +
    "-" +
    pad(parsed.getMonth() + 1) +
    "-" +
    pad(parsed.getDate()) +
    " " +
    pad(parsed.getHours()) +
    ":" +
    pad(parsed.getMinutes()) +
    ":" +
    pad(parsed.getSeconds())
  );
}

/* ------------------------------------------------------------------ */
/* public API                                                          */
/* ------------------------------------------------------------------ */

/** Full grouped structure for the drawer, straight from the offline cache. */
export async function getGroupedAssets(userid) {
  const db = await getDB(userid);
  return buildGrouped(bucketByGroup(await db.getAll("assets")));
}

/**
 * Upsert a batch of assets into the cache and return the refreshed grouped
 * structure. Pass `fullSync: true` on a complete server load so assets that no
 * longer exist server-side are pruned ("archive" data).
 */
export async function syncAssets(userid, objects = [], { fullSync = false } = {}) {
  const list = Array.isArray(objects) ? objects.filter((o) => o && o.imei) : [];
  const db = await getDB(userid);

  await upsertAssets(db, list);

  // Only prune on a genuine full load, otherwise a delta response would wipe
  // every asset that simply did not change this cycle.
  if (fullSync && list.length > 0) {
    await pruneMissing(db, new Set(list.map((o) => o.imei)));
  }

  await rebuildGroups(db);
  return getGroupedAssets(userid);
}

/** Flat, sorted asset list; optional case-insensitive search (max 100 hits). */
export async function searchAssets(userid, term = "") {
  const db = await getDB(userid);
  const all = await db.getAll("assets");

  all.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

  const query = String(term || "").trim().toLowerCase();
  if (!query) return all;

  return all
    .filter(
      (a) =>
        (a.name || "").toLowerCase().includes(query) ||
        (a.plate_number || "").toLowerCase().includes(query) ||
        (a.imei || "").toLowerCase().includes(query)
    )
    .slice(0, 100);
}

/**
 * Watermark for the next `/objects` delta call, derived from the newest
 * `dt_tracker` (the column the server actually filters on) minus a small
 * safety buffer. Returns null when the cache is empty (=> full load).
 */
export async function getWatermark(userid) {
  const db = await getDB(userid);
  const tx = db.transaction("assets", "readonly");
  const cursor = await tx
    .objectStore("assets")
    .index("dt_tracker")
    .openCursor(null, "prev");
  await tx.done;

  if (!cursor || !cursor.value || !cursor.value.dt_tracker) return null;
  return shiftBack(cursor.value.dt_tracker, WATERMARK_BUFFER_MS);
}

/** Wipe the local cache for a user (e.g. on logout). */
export async function clearUserAssets(userid) {
  const db = await getDB(userid);
  const tx = db.transaction(["assets", "groups"], "readwrite");
  await tx.objectStore("assets").clear();
  await tx.objectStore("groups").clear();
  await tx.done;
}
